/**
 * Matchmaking («Играть») — types + React Query hooks for /api/play/*.
 * Kept next to the feature rather than in the central queries.ts: the
 * play screens are the only consumers.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

export type PlayMode = "rated" | "casual";
export type TimeMode = "now" | "evening" | "any" | "exact";

export interface PlaySpot {
  id: number;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  tables_count: number | null;
}

export interface PlayRequest {
  id: number;
  status: "searching" | "matched" | "expired" | "cancelled";
  mode: PlayMode;
  time_mode: TimeMode;
  spot: PlaySpot;
  /** All picked spots, main one first. */
  spots: PlaySpot[];
  window_start: string;
  window_end: string;
  preferred_at: string;
  match_until: string;
  open_to_group: boolean;
  game_id: number | null;
  searching_since: string;
  /** Spot-search radius around each picked spot right now, km. */
  radius_km: number;
  /** Next widening step; null once at the cap. */
  radius_next_km: number | null;
  radius_next_at: string | null;
}

export interface GamePlayer {
  id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  rating: number;
  status: "active" | "invited";
  confirmed: boolean;
}

export interface GameOption {
  id: number;
  kind: "spot" | "time";
  spot: PlaySpot | null;
  starts_at: string | null;
  marked_by: number[];
  proposed_by: number | null;
}

export interface Game {
  id: number;
  mode: PlayMode;
  status: "negotiating" | "scheduled" | "completed" | "cancelled";
  open_to_join: boolean;
  spot: PlaySpot | null;
  scheduled_at: string | null;
  proposal_spot: PlaySpot | null;
  proposal_at: string | null;
  negotiation_deadline: string | null;
  required: number;
  my_status: "active" | "invited" | "left" | "declined" | "none";
  my_confirmed: boolean;
  my_invite_deadline: string | null;
  challenge_id: number | null;
  participants: GamePlayer[];
  options: GameOption[];
}

export interface PlayState {
  request: PlayRequest | null;
  games: Game[];
  has_username: boolean;
}

export interface CreatePlayRequestPayload {
  /** 1..5 spots; the first one is the main spot. */
  spot_ids: number[];
  mode: PlayMode;
  time_mode: TimeMode;
  exact_at?: string | null;
  open_to_group: boolean;
}

const keys = {
  state: ["play", "state"] as const,
  game: (id: number) => ["play", "game", id] as const,
};

// The board is shared live between players; poll while it's on screen.
const STATE_REFETCH_MS = 10_000;
const BOARD_REFETCH_MS = 3_000;

export function usePlayState({
  enabled = true,
  refetchInterval = STATE_REFETCH_MS,
}: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: keys.state,
    queryFn: () => api.get<PlayState>("/play/state"),
    // Observers share the query; React Query polls at the shortest interval.
    refetchInterval,
    enabled,
  });
}

export function useGame(id: number | null) {
  return useQuery({
    queryKey: keys.game(id ?? 0),
    queryFn: () => api.get<Game>(`/play/games/${id}`),
    enabled: id !== null,
    refetchInterval: (q) =>
      q.state.data?.status === "negotiating" || q.state.data?.my_status === "invited"
        ? BOARD_REFETCH_MS
        : STATE_REFETCH_MS,
  });
}

export function useCreatePlayRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePlayRequestPayload) =>
      api.post<PlayState>("/play/requests", payload),
    onSuccess: (data) => qc.setQueryData(keys.state, data),
  });
}

export function useCancelPlayRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<PlayState>(`/play/requests/${id}/cancel`),
    onSuccess: (data) => {
      qc.setQueryData(keys.state, data);
      void qc.invalidateQueries({ queryKey: ["play", "game"] });
    },
  });
}

type BoardAction =
  | { kind: "mark"; option_id: number; on: boolean }
  | { kind: "add-spot"; spot_id: number }
  | { kind: "add-time"; starts_at: string }
  | { kind: "confirm" }
  | { kind: "leave" }
  | { kind: "invite"; accept: boolean };

function boardRequest(id: number, a: BoardAction): Promise<Game> {
  switch (a.kind) {
    case "mark":
      return api.post<Game>(`/play/games/${id}/marks`, { option_id: a.option_id, on: a.on });
    case "add-spot":
      return api.post<Game>(`/play/games/${id}/options`, { spot_id: a.spot_id });
    case "add-time":
      return api.post<Game>(`/play/games/${id}/options`, { starts_at: a.starts_at });
    case "confirm":
      return api.post<Game>(`/play/games/${id}/confirm`);
    case "leave":
      return api.post<Game>(`/play/games/${id}/leave`);
    case "invite":
      return api.post<Game>(`/play/games/${id}/invite`, { accept: a.accept });
  }
}

export function useBoardAction(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: BoardAction) => boardRequest(id, a),
    onSuccess: (data) => {
      qc.setQueryData(keys.game(id), data);
      void qc.invalidateQueries({ queryKey: keys.state });
      // A scheduled rated game becomes a challenge on the home screen.
      void qc.invalidateQueries({ queryKey: ["challenges"] });
    },
  });
}

export function playerName(p: { first_name: string | null; last_name: string | null; username: string | null }): string {
  return [p.first_name, p.last_name].filter(Boolean).join(" ") || p.username || "игрок";
}

// ---------- admin -------------------------------------------------------------

export interface AdminPlayOverview {
  searching: number;
  negotiating: number;
  scheduled: number;
  requests: {
    id: number;
    user: string;
    spot: string;
    mode: PlayMode;
    time_mode: TimeMode;
    status: string;
    open_to_group: boolean;
    game_id: number | null;
    created_at: string;
    searching_since: string;
    match_until: string;
  }[];
  games: {
    id: number;
    mode: PlayMode;
    status: string;
    open_to_join: boolean;
    spot: string | null;
    scheduled_at: string | null;
    players: string[];
    created_at: string;
  }[];
}

export function useAdminPlayOverview() {
  return useQuery({
    queryKey: ["play", "admin"],
    queryFn: () => api.get<AdminPlayOverview>("/play/admin/overview"),
    refetchInterval: STATE_REFETCH_MS,
  });
}
